'use client';

import { useState } from 'react';
import { cn } from '../../lib/utils';

/**
 * Accordion Item Component
 */
function AccordionItem({ item, isOpen, onClick, index }) {
  return (
    <div className="border-b border-gray-200 dark:border-gray-700">
      <button
        onClick={onClick}
        className={cn(
          'w-full flex items-center justify-between py-5 px-6 text-left',
          'hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent'
        )}
        aria-expanded={isOpen}
        aria-controls={`accordion-content-${index}`}
        id={`accordion-header-${index}`}
      >
        <span className="font-heading text-gray-600 dark:text-gray-300 font-bold text-lg pr-8">
          {item.question}
        </span>
        <svg
          className={cn(
            'w-5 h-5 flex-shrink-0 transition-transform duration-300',
            isOpen && 'rotate-180'
          )}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <div
        id={`accordion-content-${index}`}
        role="region"
        aria-labelledby={`accordion-header-${index}`}
        className={cn(
          'overflow-hidden transition-all duration-300',
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        )}
      >
        <div className="px-6 pb-5 text-gray-600 dark:text-gray-300">
          {item.answer}
        </div>
      </div>
    </div>
  );
}

/**
 * Accordion Component
 *
 * @param {Object} props
 * @param {Array} props.items - Array of { question, answer }
 * @param {boolean} props.allowMultiple - Allow multiple items open at once
 * @param {number} props.defaultOpen - Index of default open item
 */
export function Accordion({ items, allowMultiple = false, defaultOpen = null, className }) {
  const [openItems, setOpenItems] = useState(
    defaultOpen !== null ? [defaultOpen] : []
  );

  const toggleItem = (index) => {
    if (allowMultiple) {
      setOpenItems((prev) =>
        prev.includes(index)
          ? prev.filter((i) => i !== index)
          : [...prev, index]
      );
    } else {
      setOpenItems((prev) =>
        prev.includes(index) ? [] : [index]
      );
    }
  };

  return (
    <div className={cn('bg-white dark:bg-gray-900 rounded-lg shadow-sm', className)}>
      {items?.map((item, index) => (
        <AccordionItem
          key={index}
          item={item}
          index={index}
          isOpen={openItems.includes(index)}
          onClick={() => toggleItem(index)}
        />
      ))}
    </div>
  );
}

/**
 * FAQ Section Component - Pre-styled for FAQ usage
 */
export function FAQSection({ headline, subheadline, items, className }) {
  return (
    <section className={cn('py-20 md:py-32', className)}>
      <div className="container mx-auto px-4 md:px-8 lg:px-12">
        {/* Header */}
        {(headline || subheadline) && (
          <div className="max-w-2xl mx-auto text-center mb-12 md:mb-16">
            {subheadline && (
              <p className="text-accent-dark dark:text-accent font-semibold uppercase tracking-wider text-sm mb-2">
                {subheadline}
              </p>
            )}
            {headline && (
              <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl dark:text-white">
                {headline}
              </h2>
            )}
          </div>
        )}

        {/* Accordion */}
        <div className="max-w-3xl mx-auto">
          <Accordion items={items || []} />
        </div>
      </div>
    </section>
  );
}

export default Accordion;
